import { useState, useEffect } from "react";
import styles from "./DealerOnboarding.module.css";
import DealerDetailsModal from "./DealerModal";
import apiService from "../../services/apiService";
import DealerRegistrationForm from "./DealerCreationForm";
import EditDealerBankModal from "./EditBankDetailsModal";

function formatDate(isoString) {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) throw new Error("Invalid date");

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const hours = date.getHours() % 12 || 12;
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = date.getHours() >= 12 ? "PM" : "AM";

    const suffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
        ? "rd"
        : "th";

    return `${day}${suffix} ${month} ${hours}:${minutes} ${ampm}`;
  } catch (e) {
    return "NA";
  }
}

export default function DealerOnboarding() {
  const [pendingDealers, setDealers] = useState([]);
  const [registeredDealers, setRegisteredDealers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [modalDealer, setModalDealer] = useState(null);
  const [modalDealerRegistration, setModalDealerRegistration] = useState(null);
  const [modalBankEdit, setModalBankEdit] = useState(null);

  const ITEMS_PER_PAGE = 5;

  useEffect(() => {
    async function fetchDealers() {
      try {
        const response = await apiService.post("/getOnboardingDealers");

        if (!response.error) {
          setDealers(response.dealers);
          setFiltered(response.dealers);

          setRegisteredDealers(response.registeredDealers);
        }
      } catch (error) {
        console.error("Error fetching dealers:", error);
      }
    }
    fetchDealers();
  }, []);

  // 🔍 Search
  useEffect(() => {
    const filteredList = pendingDealers.filter((d) =>
      d.name.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(filteredList);
    setPage(1);
  }, [search, pendingDealers]);

  // 🔁 Sort
  const sortByName = () => {
    const sorted = [...filtered].sort((a, b) => {
      if (sortOrder === "asc") return a.name.localeCompare(b.name);
      return b.name.localeCompare(a.name);
    });
    setFiltered(sorted);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const paginated = filtered.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <div className={styles.wrapper}>
      <h1 className={styles.title}>Pending Dealers</h1>

      <div className={styles.toolbar}>
        <input
          type="text"
          placeholder="Search dealer name..."
          className={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          className={styles.addBtn}
          style={{
            marginLeft: "1rem",
            padding: "0.5rem 1.2rem",
            fontWeight: 600,
            background: "#3182ce",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
          onClick={() => setModalDealerRegistration(true)}
        >
          Register New Dealer
        </button>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th onClick={sortByName} className={styles.sortable}>
                Dealer Name {sortOrder === "asc" ? "▲" : "▼"}
              </th>
              <th>Created</th>
              <th>Last Update</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Details</th>
              <th>Payment</th>
              <th>Documents</th>
              <th>Contract</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((dealer) => (
              <tr key={dealer.id}>
                <td>{dealer.name}</td>
                <td>{formatDate(dealer.createdAt)}</td>
                <td>{formatDate(dealer.updatedAt)}</td>
                <td>{dealer.phone}</td>
                <td>{dealer.email}</td>
                <td>
                  <span
                    className={`${styles.status} ${
                      styles[dealer.stage_validation_states.details.status]
                    }`}
                  >
                    {dealer.stage_validation_states.details.status}
                  </span>
                </td>
                <td>
                  <span
                    className={`${styles.status} ${
                      styles[dealer.stage_validation_states.payment.status]
                    }`}
                  >
                    {dealer.stage_validation_states.payment.status}
                  </span>
                </td>
                <td>
                  <span
                    className={`${styles.status} ${
                      styles[dealer.stage_validation_states.documents.status]
                    }`}
                  >
                    {dealer.stage_validation_states.documents.status}
                  </span>
                </td>
                <td>
                  <span
                    className={`${styles.status} ${
                      styles[dealer.stage_validation_states.contract.status]
                    }`}
                  >
                    {dealer.stage_validation_states.contract.status}
                  </span>
                </td>
                <td>
                  <button
                    className={styles.viewBtn}
                    onClick={() => setModalDealer(dealer)}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={styles.pagination}>
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page === 1}
        >
          Prev
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
          disabled={page === totalPages}
        >
          Next
        </button>
      </div>

      <h2 className={styles.subtitle}>Registered Dealers</h2>
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Dealer Name</th>
              <th>Registered On</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {registeredDealers.map((dealer) => (
              <tr key={dealer.id}>
                <td>{dealer.name}</td>
                <td>{formatDate(dealer.createdAt)}</td>
                <td>{dealer.phone}</td>
                <td>{dealer.email}</td>
                <td>
                  <button
                    className={styles.viewBtn}
                    onClick={() => setModalBankEdit(dealer)}
                  >
                    Edit Bank
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalDealer && (
        <DealerDetailsModal
          dealer={modalDealer}
          onClose={() => setModalDealer(null)}
        />
      )}

      {modalDealerRegistration && (
        <DealerRegistrationForm
          onClose={() => setModalDealerRegistration(null)}
        />
      )}

      {modalBankEdit && (
        <EditDealerBankModal
          dealer={modalBankEdit}
          onClose={() => setModalBankEdit(null)}
        />
      )}
    </div>
  );
}
